import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@hooks/useAccessToken';
import {
  isRefusedTransferRequest,
  type TransferRecommendationQuery,
  type TransferRecommendationResponse
} from '@api-service/recommendations';
import { requestTransferRecommendations } from '@api-service/transfer-recommendation-runs';

const TWO_MINUTES_IN_MS = 2 * 60 * 1000;
const RETRY_LIMIT = 2;

export const TRANSFER_RECOMMENDATIONS_QUERY_KEY = [
  'transferRecommendations'
] as const;

export type UseTransferRecommendationsOptions = TransferRecommendationQuery & {
  enabled?: boolean;
};

export const useTransferRecommendations = (
  options: UseTransferRecommendationsOptions
) => {
  const { enabled = false, ...query } = options;
  const { accessToken, accessTokenIsFetching } = useAccessToken();

  return useQuery<TransferRecommendationResponse, Error>({
    queryKey: [...TRANSFER_RECOMMENDATIONS_QUERY_KEY, query],
    // Resolves only once recommendations exist, which for a solve that had to
    // be queued is long after the request that started it returned. Keeping the
    // whole wait inside one query is what keeps the query's own in-flight state
    // honest, so every consumer's loading state still covers it.
    //
    // The signal is react-query's: it aborts when this query is cancelled or
    // superseded, which stops the poll loop and discards a run the UI has
    // stopped waiting on. A result that does arrive late lands under its own
    // query key, never on top of a newer one.
    queryFn: ({ signal }) => {
      if (!accessToken) {
        throw new Error('No access token available');
      }
      return requestTransferRecommendations({ accessToken, signal, ...query });
    },
    enabled: enabled && Boolean(accessToken) && !accessTokenIsFetching,
    staleTime: TWO_MINUTES_IN_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    // A request the solver can never satisfy, or one the API rejects as
    // malformed, will be refused for the same reason every time, so
    // retrying only delays the explanation.
    retry: (failureCount, error) =>
      !isRefusedTransferRequest(error) && failureCount < RETRY_LIMIT
  });
};
